import type { AgendaItem, ConferenceConfig } from './conference';

// Ported verbatim from microsites/womens-conference/src/lib/format.ts, except
// this base: no process.env in the widget bundle.
const FILE_BASE = 'https://my.woodsidebible.org/ministryplatformapi/files';

/** A speaker headshot or attendee photo by its MP file GUID. */
export function fileUrl(guid: string | null | undefined): string | null {
  return guid ? `${FILE_BASE}/${guid}` : null;
}

export function money(amount: number): string {
  const sign = amount < 0 ? '-' : '';
  const abs = Math.abs(amount);
  return `${sign}$${Number.isInteger(abs) ? abs : abs.toFixed(2)}`;
}

/** "17:30" -> "5:30 pm". Wall-clock at the venue; no timezone labels. */
export function clockLabel(hhmm: string): string {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  const hour12 = ((h + 11) % 12) + 1;
  const suffix = h < 12 ? 'am' : 'pm';
  return m === 0 ? `${hour12} ${suffix}` : `${hour12}:${String(m).padStart(2, '0')} ${suffix}`;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}

/** The instant an agenda item starts/ends, built from the venue-local day
 *  date and wall-clock. The config's day dates carry no offset, so we anchor
 *  with the event's own start offset; good enough for a single-venue event
 *  entirely inside one DST regime. */
export function agendaInstant(
  config: ConferenceConfig,
  item: AgendaItem,
  which: 'start' | 'end'
): Date | null {
  const day = config.days.find((d) => d.key === item.day);
  const hhmm = which === 'start' ? item.start : item.end;
  if (!day || !hhmm) return null;
  const offset = config.eventStarts.slice(-6); // e.g. "-05:00"
  return new Date(`${day.date}T${hhmm}:00${offset}`);
}

export type AgendaStatus = 'past' | 'now' | 'next' | 'upcoming';

/** Classify every agenda item against a clock. Exactly one 'now' at most,
 *  and the first future item is 'next'. */
export function agendaStatuses(
  config: ConferenceConfig,
  items: AgendaItem[],
  now: Date
): AgendaStatus[] {
  const statuses: AgendaStatus[] = items.map((item) => {
    const start = agendaInstant(config, item, 'start');
    const end = agendaInstant(config, item, 'end') ?? start;
    if (!start) return 'upcoming';
    if (end && now >= end) return 'past';
    if (now >= start) return 'now';
    return 'upcoming';
  });
  const firstUpcoming = statuses.findIndex((s) => s === 'upcoming');
  if (firstUpcoming >= 0) statuses[firstUpcoming] = 'next';
  return statuses;
}

/**
 * "November 6 and 7", or with the year, "November 6 and 7, 2026".
 *
 * Built from the day rows rather than typed, because it was typed: the hero
 * carried the literal string "November 6 and 7, 2026" while `days` held the
 * same two dates a few lines away. Moving a conference should move every
 * sentence that mentions when it is, and a date a human retypes is a date that
 * gets missed.
 *
 * Noon, not midnight, when parsing. `new Date('2026-11-06')` is UTC midnight,
 * which in Detroit is the evening of the 5th — so the naive version of this
 * renders a conference that starts a day early. The dates are wall-clock at
 * the venue (docs/timezone-architecture.md); noon is far enough from either
 * edge that no offset can move the day.
 */
export function datesLabel(
  days: { date: string }[],
  options: { year?: boolean } = {}
): string | null {
  const parts = days
    .map((day) => day.date)
    .filter(Boolean)
    .map((iso) => new Date(`${iso}T12:00:00`))
    .filter((date) => !Number.isNaN(date.getTime()));
  if (!parts.length) return null;

  const month = parts[0]!.toLocaleDateString('en-US', { month: 'long' });
  const numbers = parts.map((date) => date.getDate());
  const list =
    numbers.length === 1
      ? String(numbers[0])
      : `${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}`;
  const year = options.year ? `, ${parts[0]!.getFullYear()}` : '';
  return `${month} ${list}${year}`;
}
